import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';
import { Order } from '../../types';
import './Customer.css';

const OrderHistory: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await authAPI.getOrderHistory();
        setOrders(data);
      } catch (err) {
        console.error('Failed to fetch orders:', err);
        setError(t('customer.fetch_error') || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [t]);

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'delivered':
        return 'status-badge status-delivered';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'processing':
        return 'status-badge status-processing';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge status-pending';
    }
  };

  if (loading) {
    return (
      <div className="customer-container">
        <div className="customer-content">
          <div className="loading-state">Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-container">
      <div className="customer-content">
        <h1 className="customer-title">{t('customer.order_history') || 'অর্ডার ইতিহাস'}</h1>

        {error && (
          <div className="error-message">{error}</div>
        )}

        {orders.length === 0 ? (
          <div className="empty-state-large">
            <div className="empty-icon">📦</div>
            <h2>{t('customer.no_orders') || 'কোন অর্ডার নেই'}</h2>
            <p>{t('customer.no_orders_message') || 'আপনি এখনও কোন অর্ডার করেননি'}</p>
            <Link to="/products" className="shop-now-button">
              {t('customer.start_shopping') || 'কেনাকাটা শুরু করুন'}
            </Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div className="order-number">
                    <strong>{t('customer.order') || 'অর্ডার'} #{order.order_number}</strong>
                    <span className={getStatusBadgeClass(order.status)}>
                      {order.status_display || order.status}
                    </span>
                  </div>
                  <div className="order-date">
                    {new Date(order.created_at!).toLocaleDateString('bn-BD', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                <div className="order-body">
                  <div className="order-info">
                    <div className="info-row">
                      <span className="info-label">{t('customer.items') || 'আইটেম'}:</span>
                      <span className="info-value">{order.items?.length || 0}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t('customer.total') || 'মোট'}:</span>
                      <span className="info-value">৳{order.final_amount?.toFixed(2)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t('customer.payment') || 'পেমেন্ট'}:</span>
                      <span className="info-value">
                        {order.payment_method === 'cash_on_delivery' 
                          ? (t('customer.cod') || 'ক্যাশ অন ডেলিভারি')
                          : order.payment_method}
                      </span>
                    </div>
                  </div>

                  <div className="order-items">
                    <h4>{t('customer.order_items') || 'অর্ডার আইটেম'}:</h4>
                    <ul>
                      {order.items?.map((item, index) => (
                        <li key={index}>
                          {item.product_name} × {item.quantity} - ৳{item.subtotal?.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="order-address">
                    <h4>{t('customer.shipping_address') || 'শিপিং ঠিকানা'}:</h4>
                    <p>
                      {order.shipping_address}<br />
                      {t('customer.phone') || 'ফোন'}: {order.customer_phone}
                    </p>
                  </div>
                </div>

                <div className="order-footer">
                  <Link 
                    to={`/track-order?order=${order.order_number}`} 
                    className="track-order-button"
                  >
                    {t('customer.track_order') || 'অর্ডার ট্র্যাক করুন'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
